import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Td,
  Text,
  Textarea,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useDeleteAllFilesFromTest, useUpdateTest } from "api/appointments";
import { checkImage } from "helpers";
import React, { useState, useRef, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { BiEdit } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FilePreview, LocalFilePreview } from "./FilePreview";

export function PatientTestItem({ test, appointment, patient, disabled }: any) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const imageModal = useDisclosure();
  const uploadRef = useRef<any>(null);

  const [imageSrc, setImageSrc] = useState<any | undefined>();
  const [image, setImage] = useState<any | null>(null);

  const deleteAllReportsModal = useDisclosure();

  function openImagePreview(src: any) {
    if (checkImage(src.name)) {
      setImageSrc(src);
      imageModal.onOpen();
    }
  }

  const { handleSubmit, register, control, errors, watch, setValue } = useForm({
    defaultValues: {
      status: test.status,
      remarks: test.remarks,
    },
  });

  const [updateTest, updateTestInfo] = useUpdateTest();

  const watchStatus = watch("status");

  useEffect(() => {
    if (!image?.length) {
      setValue("document", undefined);
    }
  }, [image]);

  async function onSubmit(values: any) {
    if (image) {
      const docs: File = image.map((i: any) => i.img);
      values.document = docs;
    }
    await updateTest({ ...values, patient, test, appointment });
    onClose();
    setImage(null);
    onClose();
  }

  return (
    <>
      <Tr key={test.id}>
        <Td py="8">{test.name}</Td>
        <Td>
          <Badge colorScheme={test.status === "active" ? "blue" : "green"}>
            {test.status}
          </Badge>
        </Td>
        <Td>{test.remarks || "---"}</Td>
        <Td textAlign="right">
          {test.files?.length ? (
            test.files ? (
              <>
                <Modal isOpen={imageModal.isOpen} onClose={imageModal.onClose}>
                  <ModalOverlay />
                  <ModalContent
                    onClick={imageModal.onClose}
                    boxShadow="none"
                    height="80vh"
                    bg="rgba(0,0,0,0.4)"
                  >
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      {imageSrc?.url && (
                        <Image
                          src={imageSrc.url}
                          maxH={"80vh"}
                          width="auto"
                          fallbackSrc={`https://via.placeholder.com/150/?text=${imageSrc.name
                            .split(".")[1]
                            .toUpperCase()}`}
                        />
                      )}
                    </Flex>
                  </ModalContent>
                </Modal>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Grid
                    gap={2}
                    templateColumns="repeat(auto-fit, 50px)"
                    justifyContent="flex-end"
                    autoFlow="dense"
                    flexGrow={1}
                    pt={2}
                    wrap="wrap"
                  >
                    {test.files.map((file: any) => (
                      <FilePreview
                        key={file.url}
                        file={file}
                        appointment={appointment}
                        patient={patient}
                        test={test}
                        openImagePreview={openImagePreview}
                        size={50}
                        showDelete={false}
                      />
                    ))}
                  </Grid>
                  <IconButton
                    p={0}
                    color="black.500"
                    variant="ghost"
                    onClick={onOpen}
                    aria-label="edit report"
                    disabled={disabled}
                    icon={<BiEdit />}
                  />
                  <IconButton
                    p={0}
                    color="red.300"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => deleteAllReportsModal.onOpen()}
                    aria-label="delete photo"
                    icon={<RiDeleteBin6Line />}
                  />
                </Stack>
              </>
            ) : (
              "Not Set"
            )
          ) : (
            <Button
              onClick={onOpen}
              leftIcon={<FaPlus />}
              hidden={patient.files?.length}
              disabled={disabled}
            >
              Upload Report
            </Button>
          )}
        </Td>
      </Tr>
      <DeleteTestFilesModal
        test={test}
        appointment={appointment}
        patientId={patient.profileId}
        isOpen={deleteAllReportsModal.isOpen}
        onClose={deleteAllReportsModal.onClose}
      />

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalHeader bg="modalHeader.500" color="white">
              Update Test Info
            </ModalHeader>
            <ModalCloseButton color="faded.500" top="4" />
            <ModalBody>
              <Stack>
                <FormControl isInvalid={errors.status}>
                  <FormLabel htmlFor="status">Status</FormLabel>
                  <Controller
                    as={
                      <Select
                        disabled={test.status && test.status === "completed"}
                        placeholder="Select option"
                        ref={register}
                      >
                        <option value="active">Active</option>
                        <option value="rejected">Rejected</option>
                        <option value="completed">Completed</option>
                      </Select>
                    }
                    name="status"
                    control={control}
                    rules={{ required: true }}
                    defaultValue=""
                  />

                  <FormErrorMessage>Please Select a status</FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="document">Document</FormLabel>
                  <Stack direction="row">
                    {test.files?.map((file: any) => (
                      <FilePreview
                        key={file.url}
                        file={file}
                        appointment={appointment}
                        patient={patient}
                        test={test}
                        openImagePreview={openImagePreview}
                        size={100}
                        showDelete={!(test.status === "completed")}
                      />
                    ))}
                    {image &&
                      image.map((img: any, index: any) => (
                        <LocalFilePreview
                          key={index}
                          file={img}
                          image={image}
                          setImage={setImage}
                          index={index}
                          size={100}
                        />
                      ))}
                  </Stack>
                  <input
                    ref={(e) => {
                      uploadRef.current = e;
                      register(e, {
                        required:
                          watchStatus === "completed" &&
                          (!test.files || test.files.length === 0),
                      });
                    }}
                    onChange={(e) => {
                      let img = e.target?.files;
                      let imgs: any = [];
                      if (img) {
                        for (var i = 0; i < img?.length; i++) {
                          imgs.push({
                            img: img[i],
                            url: URL.createObjectURL(img[i]),
                          });
                        }
                      }
                      setImage(imgs);
                    }}
                    style={{ display: "none", marginTop: 10 }}
                    name="document"
                    placeholder="Select Document"
                    type="file"
                    accept="application/pdf"
                    multiple
                  />

                  <Button
                    mr={3}
                    onClick={() => uploadRef.current?.click()}
                    h="32px"
                    background="white"
                    marginTop="3px"
                    color="black"
                    border="1px solid black"
                  >
                    Upload
                  </Button>
                  {errors && Object.values(errors)?.length ? (
                    <div style={{ color: "red" }}>Please upload document.</div>
                  ) : null}
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="remarks">Remarks</FormLabel>
                  <Textarea
                    ref={register}
                    name="remarks"
                    placeholder="Enter Remarks"
                    type="text"
                  />
                </FormControl>
              </Stack>
            </ModalBody>
            <ModalFooter justifyContent="flex-start">
              <Button type="submit" mr={3} isLoading={updateTestInfo.isLoading}>
                Update
              </Button>
              <Button variant="outline" colorScheme="gray" onClick={onClose}>
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
}

function DeleteTestFilesModal({
  onClose,
  appointment,
  test,
  patientId,
  isOpen,
}: any) {
  const [deleteAllFilesFromTest, deleteAllFilesFromTestInfo] =
    useDeleteAllFilesFromTest();

  const cancelRef = React.useRef<any>();
  async function onSubmit() {
    await deleteAllFilesFromTest({
      test,
      appointment,
      patientId,
    });
    onClose();
  }
  return (
    <AlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={isOpen}
    >
      <AlertDialogOverlay />
      <AlertDialogContent maxW="50vw">
        <Flex direction="column">
          <AlertDialogHeader bg="white" color="black" borderTopRadius="4px">
            Delete reports?
          </AlertDialogHeader>
          <AlertDialogCloseButton color="richText.500" fontSize="14" />
          <Divider color="border.500" height="2px"></Divider>
          <AlertDialogBody marginY={4}>
            <Text size="sm" color="settingText.500">
              {" "}
              Are you sure you want to delete all reports from this test?
            </Text>
          </AlertDialogBody>
          <Divider></Divider>
          <AlertDialogFooter>
            <Button
              bg="red.300"
              onClick={onSubmit}
              isLoading={deleteAllFilesFromTestInfo.isLoading}
            >
              Delete
            </Button>
            <Button
              ml={3}
              variant="outline"
              border="2px"
              borderColor="border.500"
              ref={cancelRef}
              onClick={onClose}
            >
              Cancel
            </Button>
          </AlertDialogFooter>
        </Flex>
      </AlertDialogContent>
    </AlertDialog>
  );
}
